import { useEffect, useCallback, useRef } from 'react';
import { createTableRow, cloneTableBlock } from '@/lib/table-utils';
import { usePastePreview } from '../usePastePreview';
import {
    parseClipboardTable,
    hasMultipleCells,
    detectClipboardSource,
    TABLE_EDITOR_SELECTOR,
} from './parseClipboardTable';
import type { CellPosition } from './TableSelection';
import type { TableBlock } from '@/types/report-payload';
import type { RefObject } from 'react';

type UseTablePasteProps = {
    tableEditorRef: RefObject<HTMLDivElement | null>;
    disabled: boolean;
    editingCell: CellPosition | null;
    selection: { start: CellPosition; end: CellPosition } | null;
    block: TableBlock;
    columnWidths: number[];
    updateBlock: (next: TableBlock) => void;
};

/**
 * Applies parsed TSV data onto an existing table block starting from the
 * anchor selection. Returns the updated block (deep clone).
 */
const applyPasteToBlock = (
    parsed: string[][],
    selection: { start: CellPosition; end: CellPosition },
    block: TableBlock,
    columnWidths: number[],
): TableBlock => {
    const anchorSection = selection.start.section;
    const anchorRow = selection.start.rowIndex;
    const anchorCol = selection.start.colIndex;
    const totalColumns = block.header.cells.length;

    const next = cloneTableBlock(block, columnWidths);

    for (let pasteRow = 0; pasteRow < parsed.length; pasteRow++) {
        const rowValues = parsed[pasteRow];

        for (let pasteCol = 0; pasteCol < rowValues.length; pasteCol++) {
            const targetCol = anchorCol + pasteCol;

            // Truncate: don't paste beyond existing columns
            if (targetCol >= totalColumns) continue;

            const value = rowValues[pasteCol];

            if (anchorSection === 'header') {
                // Only first paste row goes to header; rest overflow into body
                if (pasteRow === 0) {
                    const cell = next.header.cells[targetCol];
                    if (cell && !cell.mergedInto) {
                        cell.value = value;
                    }
                } else {
                    const bodyRowIndex = pasteRow - 1;
                    // Auto-add rows if needed
                    while (next.rows.length <= bodyRowIndex) {
                        next.rows.push(createTableRow(totalColumns));
                    }
                    const cell = next.rows[bodyRowIndex].cells[targetCol];
                    if (cell && !cell.mergedInto) {
                        cell.value = value;
                    }
                }
            } else {
                // Body paste
                const targetRow = anchorRow + pasteRow;

                // Auto-add rows if needed
                while (next.rows.length <= targetRow) {
                    next.rows.push(createTableRow(totalColumns));
                }

                const cell = next.rows[targetRow].cells[targetCol];
                if (!cell) continue;

                if (cell.mergedInto) {
                    // Skip merged cells — paste around them
                    continue;
                }

                cell.value = value;
            }
        }
    }

    return next;
};

export const useTablePaste = ({
    tableEditorRef,
    disabled,
    editingCell,
    selection,
    block,
    columnWidths,
    updateBlock,
}: UseTablePasteProps) => {
    const { showPreviewOrApply } = usePastePreview();

    // Store frequently-changing values in a ref so the listener stays stable
    const stateRef = useRef({ editingCell, selection, block, columnWidths });
    stateRef.current = { editingCell, selection, block, columnWidths };

    const handlePaste = useCallback(
        (event: ClipboardEvent) => {
            if (event.defaultPrevented) return;

            const { editingCell, selection } = stateRef.current;

            // If user is editing a cell (input/textarea focused), let browser handle it
            if (editingCell) return;

            // Must have a selected cell to anchor the paste
            if (!selection) return;

            // Only handle when focus is inside this table editor
            const container = tableEditorRef.current;
            if (!container) return;
            const outerDiv = container.closest(TABLE_EDITOR_SELECTOR);
            const active = document.activeElement;
            if (!outerDiv || !active || !outerDiv.contains(active)) return;

            const html = event.clipboardData?.getData('text/html');
            const text = event.clipboardData?.getData('text/plain');

            const parsed = parseClipboardTable(html, text);
            if (parsed.length === 0) return;

            const source = detectClipboardSource(html ?? '');

            event.preventDefault();

            const applyFn = () => {
                // Read from stateRef again — the modal may have been open for
                // a while, and the block/selection could have changed.
                const current = stateRef.current;
                const nextBlock = applyPasteToBlock(
                    parsed,
                    current.selection ?? selection,
                    current.block,
                    current.columnWidths,
                );
                updateBlock(nextBlock);
            };

            // A single value dropped into a single cell needs no preview. This
            // stays a cell-count question on purpose: pasting prose into a cell
            // is a legitimate one-cell edit, unlike on the canvas where it
            // decides between a table block and a text block.
            if (!hasMultipleCells(parsed)) {
                applyFn();
                return;
            }

            showPreviewOrApply(parsed, source, applyFn);
        },
        [tableEditorRef, updateBlock, showPreviewOrApply],
    );

    useEffect(() => {
        if (disabled) return;

        document.addEventListener('paste', handlePaste);
        return () => document.removeEventListener('paste', handlePaste);
    }, [disabled, handlePaste]);
};
